import { useState } from 'react'
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Cloud,
  FileText,
  FolderOpen,
  Loader2,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Trash2,
  X,
  Zap,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  useFolderWatches,
  useCreateFolderWatch,
  useDeleteFolderWatch,
  useToggleFolderWatch,
  useTriggerPoll,
  useWatchFiles,
  type FolderWatch,
  type CreateFolderWatchPayload,
} from '../hooks/useFolderWatches'

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const PROVIDER_LABELS: Record<string, { label: string; color: string }> = {
  s3:           { label: 'AWS S3 / R2',    color: 'text-orange-400 bg-orange-500/10 border-orange-500/20' },
  dropbox:      { label: 'Dropbox',        color: 'text-blue-400   bg-blue-500/10   border-blue-500/20' },
  google_drive: { label: 'Google Drive',   color: 'text-green-400  bg-green-500/10  border-green-500/20' },
  sftp:         { label: 'SFTP',           color: 'text-purple-400 bg-purple-500/10 border-purple-500/20' },
}

function ProviderBadge({ provider }: { provider: string }) {
  const cfg = PROVIDER_LABELS[provider] ?? { label: provider, color: 'text-[--km-text-muted] bg-[--km-surface-3] border-[--km-border]' }
  return (
    <span className={clsx('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium', cfg.color)}>
      <Cloud size={10} />
      {cfg.label}
    </span>
  )
}

function JobStatusDot({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-[--km-text-disabled]">—</span>
  const map: Record<string, string> = {
    completed: 'bg-emerald-400',
    failed:    'bg-red-400',
    mapping:   'bg-amber-400',
    pending:   'bg-[--km-text-disabled]',
  }
  const label: Record<string, string> = {
    completed: 'Completato', failed: 'Fallito',
    mapping: 'Mapping', pending: 'In attesa',
  }
  const dot = map[status] ?? 'bg-indigo-400'
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-[--km-text-secondary]">
      <span className={clsx('h-1.5 w-1.5 rounded-full', dot)} />
      {label[status] ?? status}
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// File list per una singola watch
// ─────────────────────────────────────────────────────────────────────────────

function WatchFilesPanel({ watchId }: { watchId: string }) {
  const { data: files, isLoading } = useWatchFiles(watchId)

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 px-4 text-[--km-text-muted]">
        <Loader2 size={14} className="animate-spin" />
        <span className="text-xs">Caricamento file…</span>
      </div>
    )
  }

  if (!files || files.length === 0) {
    return (
      <div className="py-4 px-4 text-center">
        <p className="text-xs text-[--km-text-muted]">Nessun file rilevato ancora. Avvia il polling per scoprire i file nella cartella.</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="border-b border-[--km-border]">
            <th className="py-2 px-4 text-[10px] font-semibold uppercase tracking-widest text-[--km-text-disabled]">File</th>
            <th className="py-2 px-4 text-[10px] font-semibold uppercase tracking-widest text-[--km-text-disabled] hidden sm:table-cell">Dimensione</th>
            <th className="py-2 px-4 text-[10px] font-semibold uppercase tracking-widest text-[--km-text-disabled] hidden md:table-cell">Ultima modifica</th>
            <th className="py-2 px-4 text-[10px] font-semibold uppercase tracking-widest text-[--km-text-disabled]">Stato job</th>
          </tr>
        </thead>
        <tbody>
          {files.map((f) => (
            <tr key={f.file_path} className="border-b border-[--km-border] last:border-0 hover:bg-[--km-surface-2]">
              <td className="py-2 px-4">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={12} className="shrink-0 text-[--km-primary]" />
                  <span className="truncate text-[--km-text-primary] max-w-[180px]" title={f.file_path}>
                    {f.file_name}
                  </span>
                </div>
              </td>
              <td className="py-2 px-4 hidden sm:table-cell text-[--km-text-muted]">
                {formatBytes(f.file_size)}
              </td>
              <td className="py-2 px-4 hidden md:table-cell text-[--km-text-muted]">
                {formatDate(f.remote_modified_at)}
              </td>
              <td className="py-2 px-4">
                <JobStatusDot status={f.job_status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Watch row
// ─────────────────────────────────────────────────────────────────────────────

function WatchRow({ watch }: { watch: FolderWatch }) {
  const [expanded, setExpanded] = useState(false)
  const [confirming, setConfirming] = useState(false)

  const toggle = useToggleFolderWatch()
  const del = useDeleteFolderWatch()
  const poll = useTriggerPoll()

  const isPolling = poll.isPending

  return (
    <div className="border-b border-[--km-border] last:border-0">
      {/* Header row */}
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-[--km-surface-2] transition-colors">
        {/* Expand toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-[--km-text-muted] hover:text-[--km-text-primary] transition-colors"
          aria-label={expanded ? 'Comprimi' : 'Espandi'}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        {/* Status dot */}
        <span
          className={clsx(
            'h-2 w-2 rounded-full shrink-0',
            watch.is_active ? 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)]' : 'bg-[--km-text-disabled]',
          )}
          title={watch.is_active ? 'Attiva' : 'Inattiva'}
        />

        {/* Name */}
        <span className="flex-1 min-w-0 font-medium text-sm text-[--km-text-primary] truncate">
          {watch.name}
        </span>

        {/* Provider badge */}
        <ProviderBadge provider={watch.provider} />

        {/* Interval */}
        <span className="text-xs text-[--km-text-muted] hidden sm:block shrink-0">
          ogni {watch.poll_interval} min
        </span>

        {/* Last polled */}
        <span className="text-xs text-[--km-text-disabled] hidden md:block shrink-0 min-w-[120px] text-right">
          {watch.last_polled_at ? formatDate(watch.last_polled_at) : 'Mai sincronizzata'}
        </span>

        {/* Actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Poll manuale */}
          <button
            onClick={() => poll.mutate(watch.id)}
            disabled={isPolling}
            className="p-1.5 rounded-lg text-[--km-text-muted] transition-colors hover:text-indigo-400 hover:bg-indigo-500/10 disabled:opacity-50"
            title="Avvia polling adesso"
            aria-label="Sincronizza ora"
          >
            {isPolling ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
          </button>

          {/* Enable/disable */}
          <button
            onClick={() => toggle.mutate({ id: watch.id, is_active: !watch.is_active })}
            className="p-1.5 rounded-lg text-[--km-text-muted] transition-colors hover:text-[--km-text-primary] hover:bg-[--km-surface-3]"
            title={watch.is_active ? 'Metti in pausa' : 'Riattiva'}
            aria-label={watch.is_active ? 'Metti in pausa' : 'Riattiva'}
          >
            {watch.is_active ? <Pause size={13} /> : <Play size={13} />}
          </button>

          {/* Delete */}
          {confirming ? (
            <div className="flex items-center gap-1">
              <button
                onClick={() => del.mutate(watch.id)}
                className="rounded-lg bg-red-500/10 border border-red-500/20 px-2 py-1 text-xs font-medium text-red-400 hover:bg-red-500/20 transition-colors"
              >
                Elimina
              </button>
              <button
                onClick={() => setConfirming(false)}
                className="rounded-lg border border-[--km-border] px-2 py-1 text-xs text-[--km-text-muted] hover:text-[--km-text-primary] transition-colors"
              >
                Annulla
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirming(true)}
              className="p-1.5 rounded-lg text-[--km-text-muted] transition-colors hover:text-red-400 hover:bg-red-500/10"
              aria-label="Elimina cartella"
              title="Elimina"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Expanded: file list */}
      {expanded && (
        <div className="border-t border-[--km-border] bg-[--km-surface-2]/50">
          <WatchFilesPanel watchId={watch.id} />
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Create watch modal
// ─────────────────────────────────────────────────────────────────────────────

const PROVIDER_FIELDS: Record<string, Array<{ key: string; label: string; placeholder: string; secret?: boolean; required?: boolean }>> = {
  s3: [
    { key: 'bucket',            label: 'Bucket',        placeholder: 'nome-bucket', required: true },
    { key: 'prefix',            label: 'Prefisso',      placeholder: 'exports/koremargin/' },
    { key: 'access_key_id',     label: 'Access Key ID', placeholder: 'AKIA...', secret: true, required: true },
    { key: 'secret_access_key', label: 'Secret Key',    placeholder: '...', secret: true, required: true },
    { key: 'endpoint_url',      label: 'Endpoint URL',  placeholder: 'https://<id>.r2.cloudflarestorage.com (opzionale)' },
    { key: 'region',            label: 'Regione',       placeholder: 'us-east-1' },
  ],
  dropbox: [
    { key: 'access_token',  label: 'Access Token', placeholder: 'sl.B...', secret: true, required: true },
    { key: 'folder_path',   label: 'Percorso cartella', placeholder: '/exports/koremargin', required: true },
  ],
  google_drive: [
    { key: 'service_account_json', label: 'JSON Service Account', placeholder: '{"type":"service_account",...}', secret: true, required: true },
    { key: 'folder_id',            label: 'ID Cartella Drive',    placeholder: '1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs', required: true },
  ],
  sftp: [
    { key: 'host',        label: 'Host',         placeholder: 'sftp.example.com', required: true },
    { key: 'port',        label: 'Porta',        placeholder: '22' },
    { key: 'username',    label: 'Utente',       placeholder: 'sftp_user', required: true },
    { key: 'password',    label: 'Password',     placeholder: '...', secret: true },
    { key: 'private_key', label: 'Chiave privata (PEM)', placeholder: '-----BEGIN RSA PRIVATE KEY-----\n...', secret: true },
    { key: 'remote_path', label: 'Percorso remoto', placeholder: '/var/exports', required: true },
  ],
}

function CreateWatchModal({ onClose }: { onClose: () => void }) {
  const [name, setName]           = useState('')
  const [provider, setProvider]   = useState<string>('s3')
  const [interval, setInterval]   = useState(15)
  const [config, setConfig]       = useState<Record<string, string>>({})
  const [error, setError]         = useState<string | null>(null)

  const create = useCreateFolderWatch()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await create.mutateAsync({
        name,
        provider,
        config,
        poll_interval: interval,
      } satisfies CreateFolderWatchPayload)
      onClose()
    } catch (err) {
      const detail = (err as { message?: string })?.message ?? 'Errore durante la creazione'
      setError(detail)
    }
  }

  const fields = PROVIDER_FIELDS[provider] ?? []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="km-panel w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[--km-border]">
          <h2 className="text-base font-semibold text-[--km-text-primary]">
            Aggiungi cartella cloud
          </h2>
          <button
            onClick={onClose}
            className="text-[--km-text-muted] hover:text-[--km-text-primary] transition-colors"
            aria-label="Chiudi"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          {/* Nome */}
          <div>
            <label className="block text-xs font-semibold text-[--km-text-secondary] mb-1.5">
              Nome cartella
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Es: Export mensile Dropbox"
              className="w-full rounded-lg border border-[--km-border] bg-[--km-surface-3] px-3 py-2 text-sm text-[--km-text-primary] placeholder-[--km-text-disabled] focus:outline-none focus:border-[--km-primary]/50 focus:ring-1 focus:ring-[--km-primary]/20 transition"
            />
          </div>

          {/* Provider */}
          <div>
            <label className="block text-xs font-semibold text-[--km-text-secondary] mb-1.5">
              Provider
            </label>
            <select
              value={provider}
              onChange={e => { setProvider(e.target.value); setConfig({}) }}
              className="w-full rounded-lg border border-[--km-border] bg-[--km-surface-3] px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:border-[--km-primary]/50 transition"
            >
              {Object.entries(PROVIDER_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>

          {/* Intervallo */}
          <div>
            <label className="block text-xs font-semibold text-[--km-text-secondary] mb-1.5">
              Intervallo polling (minuti)
            </label>
            <input
              type="number"
              min={1}
              max={1440}
              value={interval}
              onChange={e => setInterval(Number(e.target.value))}
              className="w-full rounded-lg border border-[--km-border] bg-[--km-surface-3] px-3 py-2 text-sm text-[--km-text-primary] focus:outline-none focus:border-[--km-primary]/50 transition"
            />
          </div>

          {/* Campi provider-specifici */}
          {fields.length > 0 && (
            <div className="space-y-3 border-t border-[--km-border] pt-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-[--km-text-disabled]">
                Configurazione {PROVIDER_LABELS[provider]?.label}
              </p>
              {fields.map(field => (
                <div key={field.key}>
                  <label className="block text-xs font-semibold text-[--km-text-secondary] mb-1.5">
                    {field.label}
                    {field.required && <span className="text-red-400 ml-0.5">*</span>}
                  </label>
                  {field.key === 'service_account_json' || field.key === 'private_key' ? (
                    <textarea
                      rows={3}
                      placeholder={field.placeholder}
                      value={config[field.key] ?? ''}
                      onChange={e => setConfig(c => ({ ...c, [field.key]: e.target.value }))}
                      className="w-full rounded-lg border border-[--km-border] bg-[--km-surface-3] px-3 py-2 text-xs font-mono text-[--km-text-primary] placeholder-[--km-text-disabled] focus:outline-none focus:border-[--km-primary]/50 transition resize-none"
                    />
                  ) : (
                    <input
                      type={field.secret ? 'password' : 'text'}
                      placeholder={field.placeholder}
                      value={config[field.key] ?? ''}
                      onChange={e => setConfig(c => ({ ...c, [field.key]: e.target.value }))}
                      className="w-full rounded-lg border border-[--km-border] bg-[--km-surface-3] px-3 py-2 text-sm text-[--km-text-primary] placeholder-[--km-text-disabled] focus:outline-none focus:border-[--km-primary]/50 transition"
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Errore */}
          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2.5">
              <AlertCircle size={14} className="shrink-0 mt-0.5 text-red-400" />
              <p className="text-xs text-red-300">{error}</p>
            </div>
          )}

          {/* Note sicurezza */}
          <p className="text-[10px] text-[--km-text-disabled]">
            Le credenziali sono cifrate nel database e non vengono mai esposte nelle API.
            Il sistema testa la connessione prima di salvare.
          </p>

          {/* Submit */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-[--km-border] px-4 py-2 text-sm text-[--km-text-secondary] transition-colors hover:text-[--km-text-primary] hover:border-[--km-border-hover]"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={create.isPending}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-[--km-primary] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {create.isPending ? (
                <><Loader2 size={14} className="animate-spin" />Test connessione…</>
              ) : (
                <><CheckCircle2 size={14} />Salva cartella</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export function FolderWatches() {
  const [showModal, setShowModal] = useState(false)
  const { data: watches, isLoading, isError, refetch } = useFolderWatches()

  const active = watches?.filter(w => w.is_active).length ?? 0
  const total  = watches?.length ?? 0

  return (
    <section aria-label="Cartelle cloud" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[--km-text-primary]">Cartelle cloud</h1>
          <p className="text-sm text-[--km-text-muted] mt-0.5">
            Sincronizza automaticamente file da S3, Dropbox, Google Drive o SFTP.
            I nuovi file vengono rilevati ogni N minuti e avviano la pipeline di ingestion.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 rounded-lg border border-[--km-border] px-3 py-2 text-sm text-[--km-text-secondary] transition-colors hover:border-[--km-border-hover] hover:text-[--km-text-primary]"
          >
            <RefreshCw size={14} />
            Aggiorna
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-[--km-primary] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
          >
            <Plus size={14} />
            Aggiungi cartella
          </button>
        </div>
      </div>

      {/* Stats bar */}
      {total > 0 && (
        <div className="flex gap-4">
          <div className="km-panel px-4 py-3 flex items-center gap-2.5">
            <FolderOpen size={16} className="text-[--km-primary]" />
            <div>
              <p className="text-xs text-[--km-text-muted]">Cartelle totali</p>
              <p className="text-lg font-semibold text-[--km-text-primary] leading-tight">{total}</p>
            </div>
          </div>
          <div className="km-panel px-4 py-3 flex items-center gap-2.5">
            <CheckCircle2 size={16} className="text-emerald-400" />
            <div>
              <p className="text-xs text-[--km-text-muted]">Attive</p>
              <p className="text-lg font-semibold text-emerald-400 leading-tight">{active}</p>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="km-panel overflow-hidden">
        {isLoading && (
          <div className="flex items-center justify-center gap-2 py-16 text-[--km-text-muted]">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Caricamento cartelle…</span>
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <AlertCircle size={32} className="text-[--km-error]" />
            <p className="text-sm text-[--km-text-secondary]">Impossibile caricare le cartelle cloud.</p>
            <button onClick={() => refetch()} className="text-sm text-[--km-primary] hover:underline">
              Riprova
            </button>
          </div>
        )}

        {!isLoading && !isError && (!watches || watches.length === 0) && (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <Cloud size={40} className="text-[--km-text-disabled]" />
            <div>
              <p className="text-sm font-medium text-[--km-text-primary]">Nessuna cartella cloud configurata</p>
              <p className="text-xs text-[--km-text-muted] mt-1">
                Aggiungi una cartella per sincronizzare i file automaticamente.
              </p>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-[--km-primary] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
            >
              <Plus size={14} />
              Aggiungi cartella
            </button>
          </div>
        )}

        {!isLoading && !isError && watches && watches.length > 0 && (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[--km-border] bg-[--km-surface-2]">
              <span className="w-4 shrink-0" />
              <span className="w-2 shrink-0" />
              <span className="flex-1 text-[10px] font-semibold uppercase tracking-widest text-[--km-text-disabled]">Nome</span>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[--km-text-disabled]">Provider</span>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[--km-text-disabled] hidden sm:block">Intervallo</span>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[--km-text-disabled] hidden md:block min-w-[120px] text-right">Ultima sync</span>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[--km-text-disabled] shrink-0">Azioni</span>
            </div>
            {watches.map(w => <WatchRow key={w.id} watch={w} />)}
          </>
        )}
      </div>

      {/* Nota */}
      <div className="rounded-xl border border-[--km-border] bg-[--km-surface-2]/50 px-4 py-3">
        <p className="text-xs text-[--km-text-muted]">
          <span className="font-semibold text-[--km-text-secondary]">Come funziona:</span>
          {' '}Celery Beat controlla ogni minuto se ci sono cartelle con polling scaduto.
          Per ogni file nuovo o modificato viene avviata la pipeline di ingestion standard.
          I file già elaborati (stesso hash SHA-256) vengono ignorati automaticamente.
        </p>
      </div>

      {/* Modal */}
      {showModal && <CreateWatchModal onClose={() => setShowModal(false)} />}
    </section>
  )
}
